import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import {
  listNoteActivities,
  createNote,
  getNoteDetail,
  updateNote,
  deleteNote,
} from '../../../../../../../../services/activity';
import { getMockNotesListResponse } from './mockNotesListResponse';

/**
 * Mock temporal: false = usar endpoint real listNoteActivities.
 * Pinned y archived siguen en localStorage (solo frontend por ahora).
 */
const USE_MOCK_NOTES = false;

const NotesTabStateContext = createContext(null);
const NotesTabActionsContext = createContext(null);

const DEFAULT_PAGE_SIZE = 10;

/**
 * LocalStorage helpers for persisting pinned notes
 */
const getPinStorageKey = (contactId) => `pinned_notes_${contactId}`;

const getPinnedIdsFromStorage = (contactId) => {
  try {
    const stored = localStorage.getItem(getPinStorageKey(contactId));
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const savePinnedIdsToStorage = (contactId, pinnedIds) => {
  try {
    localStorage.setItem(getPinStorageKey(contactId), JSON.stringify(pinnedIds));
  } catch (err) {
    console.error('Error saving pinned notes to localStorage:', err);
  }
};

const getArchivedStorageKey = (contactId) => `archived_notes_${contactId}`;

const getArchivedIdsFromStorage = (contactId) => {
  try {
    const stored = localStorage.getItem(getArchivedStorageKey(contactId));
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveArchivedIdsToStorage = (contactId, archivedIds) => {
  try {
    localStorage.setItem(getArchivedStorageKey(contactId), JSON.stringify(archivedIds));
  } catch (err) {
    console.error('Error saving archived notes to localStorage:', err);
  }
};

/**
 * Ordena notas: no archivadas primero (pinned en orden de pinnedIds, luego no pinned),
 * archivadas al final. pinnedIds[0] = más recientemente pinned = primera.
 */
const sortNotesByPinAndArchive = (notes, pinnedIds) => {
  const pinnedOrder = new Map(pinnedIds.map((id, i) => [id, i]));
  const getPinnedIndex = (id) => (pinnedOrder.has(id) ? pinnedOrder.get(id) : Infinity);
  const isArchived = (n) => String(n?.status || '').toUpperCase() === 'ARCHIVED';

  return [...notes].sort((a, b) => {
    // Archivadas al final
    if (isArchived(a) && !isArchived(b)) return 1;
    if (!isArchived(a) && isArchived(b)) return -1;
    if (isArchived(a) && isArchived(b)) {
      return (new Date(b.lastUpdated || b.createdAt) || 0) - (new Date(a.lastUpdated || a.createdAt) || 0);
    }
    // No archivadas: pinned primero (orden por pinnedIds), luego no pinned por lastUpdated
    const aPinned = pinnedIds.includes(a.id);
    const bPinned = pinnedIds.includes(b.id);
    if (aPinned && !bPinned) return -1;
    if (!aPinned && bPinned) return 1;
    if (aPinned && bPinned) {
      return getPinnedIndex(a.id) - getPinnedIndex(b.id);
    }
    return (new Date(b.lastUpdated || b.createdAt) || 0) - (new Date(a.lastUpdated || a.createdAt) || 0);
  });
};

const initialFilters = {
  search: '',
  category: 'all',
};

const initialMeta = {
  total_items: 0,
  total_pages: 1,
  current_page: 1,
  per_page: DEFAULT_PAGE_SIZE,
  has_next: false,
  has_previous: false,
};

/**
 * Strip HTML tags from content
 */
const stripHtmlTags = (html) => {
  if (!html || typeof html !== 'string') return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .trim();
};

/**
 * Valores quemados para chips del footer cuando la API no devuelve estos metadatos.
 * Quitar/ajustar cuando el backend los provea.
 */
const DEMO_FOOTER_META = {
  commentCount: 2,
  attachmentCount: 1,
  linkCount: 1,
  taskCount: 3,
  tasks: ['Task 1', 'Task 2', 'Task 3'],
  sentiment: 'positive',
  delivered: true,
  recordingCount: 1,
  direction: 'Outbound',
  callDuration: '2 mins',
  endedBy: 'Customer', // Customer | Agent | System
  outcome: 'Meeting booked', // Meeting booked | Sale closed | Escalation | No resolution
};

/**
 * Map note data to normalized format
 */
let noteIdCounter = 0;
const mapNoteItem = (rawNote) => {
  const extractedNoteUuid = rawNote.note_uuid || rawNote.noteUuid || rawNote.id;
  const fallbackId = `note-${Date.now()}-${++noteIdCounter}`;
  const noteId = extractedNoteUuid || fallbackId;

  const rawTitle = rawNote.note_title || rawNote.noteTitle || rawNote.title || '';
  const rawDescription = rawNote.note_content || rawNote.description || rawNote.content || '';

  return {
    id: noteId,
    noteUuid: extractedNoteUuid || null,
    title: stripHtmlTags(rawTitle),
    description: stripHtmlTags(rawDescription),
    category: rawNote.category || rawNote.categories?.[0] || 'GENERAL',
    owner: {
      name: rawNote.owner_full_name || rawNote.ownerFullName || rawNote.owner?.name || 'Unknown',
      avatar: rawNote.owner_avatar || rawNote.ownerAvatar || rawNote.owner?.avatar || null,
    },
    createdAt: rawNote.created_at || rawNote.createdAt,
    lastUpdated: rawNote.last_update || rawNote.lastUpdate,
    isPinned: rawNote.is_pinned || rawNote.isPinned || false,
    // New fields for updated card design
    status: rawNote.status || rawNote.note_status || null,
    visibility: rawNote.visibility || rawNote.note_visibility || null,
    isTeamNote: rawNote.is_team_note || rawNote.isTeamNote || rawNote.visibility === 'TEAM',
    // API no tiene estos metadatos aún → usamos valores quemados para mostrar chips
    commentCount: rawNote.comment_count ?? rawNote.commentCount ?? rawNote.comments?.length ?? DEMO_FOOTER_META.commentCount,
    attachmentCount: rawNote.attachment_count ?? rawNote.attachmentCount ?? rawNote.attachments?.length ?? DEMO_FOOTER_META.attachmentCount,
    linkCount: rawNote.link_count ?? rawNote.linkCount ?? rawNote.links?.length ?? DEMO_FOOTER_META.linkCount,
    taskCount: rawNote.task_count ?? rawNote.taskCount ?? rawNote.tasks?.length ?? rawNote.raw?.task_count ?? DEMO_FOOTER_META.taskCount,
    tasks: (() => {
      const apiTasks = rawNote.tasks ?? rawNote.raw?.tasks;
      if (Array.isArray(apiTasks) && apiTasks.length > 0) {
        return apiTasks.map((t) => (typeof t === 'string' ? t : t?.title ?? t?.name ?? 'Task'));
      }
      return DEMO_FOOTER_META.tasks;
    })(),
    sentiment: rawNote.sentiment ?? rawNote.raw?.sentiment ?? DEMO_FOOTER_META.sentiment,
    delivered: rawNote.delivered ?? rawNote.raw?.delivered ?? DEMO_FOOTER_META.delivered,
    recordingCount: rawNote.recording_count ?? rawNote.recordingCount ?? rawNote.recordings?.length ?? rawNote.raw?.recording_count ?? DEMO_FOOTER_META.recordingCount,
    direction: rawNote.direction ?? rawNote.raw?.direction ?? DEMO_FOOTER_META.direction,
    callDuration: rawNote.call_duration ?? rawNote.callDuration ?? rawNote.raw?.call_duration ?? DEMO_FOOTER_META.callDuration,
    endedBy: rawNote.ended_by ?? rawNote.endedBy ?? rawNote.call_ended_by ?? rawNote.raw?.ended_by ?? rawNote.raw?.call_ended_by ?? DEMO_FOOTER_META.endedBy,
    outcome: rawNote.outcome ?? rawNote.raw?.outcome ?? DEMO_FOOTER_META.outcome,
    expiresAt: rawNote.expires_at || rawNote.expiresAt || null,
    raw: rawNote,
  };
};

export const NotesTabProvider = ({ children, contactId }) => {
  // State
  const [notes, setNotes] = useState([]);
  const [meta, setMeta] = useState(initialMeta);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Refs for request tracking and stable callbacks
  const fetchIdRef = useRef(0);
  const metaRef = useRef(meta);
  const notesRef = useRef(notes);
  metaRef.current = meta;
  notesRef.current = notes;

  // Fetch notes from API
  const fetchNotes = useCallback(
    async (page = 1, pageSize = DEFAULT_PAGE_SIZE) => {
      if (!contactId) return;

      const requestId = ++fetchIdRef.current;
      setLoading(true);
      setError(null);

      try {
        let response;

        if (USE_MOCK_NOTES) {
          await new Promise((r) => setTimeout(r, 400));
          const pinnedIds = getPinnedIdsFromStorage(contactId);
          const archivedIds = getArchivedIdsFromStorage(contactId);
          // Temporal: respuesta mock local (sin contrato final de backend).
          // Pinned siempre arriba, archived siempre al final en cada página.
          response = getMockNotesListResponse(
            page,
            pageSize,
            { search: filters.search, category: filters.category },
            pinnedIds,
            archivedIds
          );
        } else {
          const pinnedIds = getPinnedIdsFromStorage(contactId);
          const params = {
            party_uuid: contactId,
            page,
            page_size: pageSize,
            order_by: 'last_update',
            order_direction: 'DESC',
          };

          if (filters.category !== 'all') {
            params.filters = { category: filters.category };
          }

          if (filters.search?.trim()) {
            params.search = { keyword: filters.search.trim() };
          }

          response = await listNoteActivities(params);

          // Solo pinned se fetchean y prependen; archived participan en la paginación (última página)
          if (pinnedIds.length > 0) {
            const pageNotes = response?.body || [];
            const pageById = new Map(
              pageNotes.map((n) => [n.note_uuid || n.noteUuid || n.id, n])
            );
            const pinnedNotInPage = pinnedIds.filter((id) => !pageById.has(id));
            let fetchedById = new Map();
            if (pinnedNotInPage.length > 0) {
              const fetched = await Promise.all(
                pinnedNotInPage.map((id) => getNoteDetail(id).catch(() => null))
              );
              fetchedById = new Map(
                fetched
                  .filter(Boolean)
                  .map((r) => [r?.body?.note_uuid || r?.body?.noteUuid || r?.body?.id, r?.body])
                  .filter(([, b]) => Boolean(b))
              );
            }
            const pinnedOrdered = pinnedIds
              .map((id) => pageById.get(id) || fetchedById.get(id))
              .filter(Boolean);
            const rest = pageNotes.filter(
              (n) => !pinnedIds.includes(n.note_uuid || n.noteUuid || n.id)
            );
            response = { ...response, body: [...pinnedOrdered, ...rest] };
          }
        }

        if (requestId === fetchIdRef.current) {
          const responseMeta = response.meta || {};
          const isLastPage =
            (responseMeta.current_page ?? responseMeta.currentPage ?? page) >=
            (responseMeta.total_pages ?? responseMeta.totalPages ?? 1);

          const items = (response.body || []).map(mapNoteItem);

          const pinnedIds = getPinnedIdsFromStorage(contactId);
          const archivedIds = getArchivedIdsFromStorage(contactId);
          const MOCK_DEMO_CARD_ID = 'mock-all-icons-footer';

          const itemsWithStatus = items
            .map((note) => ({
              ...note,
              isPinned:
                pinnedIds.includes(note.id) ||
                (note.id === MOCK_DEMO_CARD_ID && note.isPinned),
              status: archivedIds.includes(note.id) ? 'ARCHIVED' : (note.status || null),
            }))
            .filter((note) => {
              if (!isLastPage && archivedIds.includes(note.id)) return false;
              return true;
            });

          const sortedItems = sortNotesByPinAndArchive(itemsWithStatus, pinnedIds);
          setNotes(sortedItems);

          setMeta({
            total_items: responseMeta.total_items || responseMeta.totalItems || items.length,
            total_pages: responseMeta.total_pages || responseMeta.totalPages || 1,
            current_page: responseMeta.current_page || responseMeta.currentPage || page,
            per_page: responseMeta.per_page || responseMeta.perPage || pageSize,
            has_next: responseMeta.has_next || responseMeta.hasNext || false,
            has_previous: responseMeta.has_previous || responseMeta.hasPrevious || false,
          });

          // Clear selection on data change
          setSelectedIds(new Set());
          setSelectAll(false);
        }
      } catch (err) {
        if (requestId === fetchIdRef.current) {
          console.error('Error fetching notes:', err);
          setError(err);
        }
      } finally {
        if (requestId === fetchIdRef.current) {
          setLoading(false);
        }
      }
    },
    [contactId, filters]
  );

  // Update filters
  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters(initialFilters);
  }, []);

  // Selection handlers
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
    setSelectAll(false);
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectAll) {
      setSelectedIds(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set(notesRef.current.map((item) => item.id));
      setSelectedIds(allIds);
      setSelectAll(true);
    }
  }, [selectAll]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSelectAll(false);
  }, []);

  // Pagination handlers (usan metaRef para evitar re-crear al cambiar meta)
  const handlePageChange = useCallback(
    (page) => {
      fetchNotes(page, metaRef.current.per_page);
    },
    [fetchNotes]
  );

  const handleItemsPerPageChange = useCallback(
    (pageSize) => {
      fetchNotes(1, pageSize);
    },
    [fetchNotes]
  );

  // CRUD Operations (usan metaRef para callbacks estables)
  const createNoteHandler = useCallback(
    async (noteData) => {
      try {
        const result = await createNote({
          entity_type: 'party',
          entity_uuid: contactId,
          ...noteData,
        });
        await fetchNotes(1, metaRef.current.per_page);
        return result;
      } catch (err) {
        console.error('Error creating note:', err);
        throw err;
      }
    },
    [contactId, fetchNotes]
  );

  const getNoteDetailHandler = useCallback(async (noteUuid) => {
    try {
      const result = await getNoteDetail(noteUuid);
      return result.body;
    } catch (err) {
      console.error('Error getting note detail:', err);
      throw err;
    }
  }, []);

  const updateNoteHandler = useCallback(
    async (noteData) => {
      try {
        const result = await updateNote(noteData);
        const m = metaRef.current;
        await fetchNotes(m.current_page, m.per_page);
        return result;
      } catch (err) {
        console.error('Error updating note:', err);
        throw err;
      }
    },
    [fetchNotes]
  );

  const deleteNoteHandler = useCallback(
    async (noteUuid) => {
      try {
        setNotes((prev) => {
          const filtered = prev.filter((item) => item.noteUuid !== noteUuid && item.id !== noteUuid);
          const pinnedIds = filtered.filter((n) => n.isPinned).map((n) => n.id);
          savePinnedIdsToStorage(contactId, pinnedIds);
          return filtered;
        });
        setSelectedIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(noteUuid);
          return newSet;
        });

        await deleteNote(noteUuid);
        const m = metaRef.current;
        await fetchNotes(m.current_page, m.per_page);
      } catch (err) {
        console.error('Error deleting note:', err);
        const m = metaRef.current;
        await fetchNotes(m.current_page, m.per_page);
        throw err;
      }
    },
    [contactId, fetchNotes]
  );

  // Pin/Unpin Note Handler — pinned reciente = primera posición
  const pinNoteHandler = useCallback(
    (noteId, isPinned) => {
      setNotes((prev) => {
        const updated = prev.map((note) =>
          note.id === noteId
            ? { ...note, isPinned, status: isPinned ? null : note.status }
            : note
        );

        const currentPinnedIds = getPinnedIdsFromStorage(contactId);
        const currentArchivedIds = getArchivedIdsFromStorage(contactId);
        let newPinnedIds;
        if (isPinned) {
          newPinnedIds = [noteId, ...currentPinnedIds.filter((id) => id !== noteId)];
          saveArchivedIdsToStorage(contactId, currentArchivedIds.filter((id) => id !== noteId));
        } else {
          newPinnedIds = currentPinnedIds.filter((id) => id !== noteId);
        }
        savePinnedIdsToStorage(contactId, newPinnedIds);

        return sortNotesByPinAndArchive(updated, newPinnedIds);
      });
    },
    [contactId]
  );

  // Archive/Unarchive Note Handler — archivada al final de última página (usa metaRef para estabilidad)
  const archiveNoteHandler = useCallback(
    (noteId, shouldArchive) => {
      const currentArchived = getArchivedIdsFromStorage(contactId);
      const newArchivedIds = shouldArchive
        ? [...currentArchived.filter((id) => id !== noteId), noteId]
        : currentArchived.filter((id) => id !== noteId);
      saveArchivedIdsToStorage(contactId, newArchivedIds);

      if (shouldArchive) {
        savePinnedIdsToStorage(
          contactId,
          getPinnedIdsFromStorage(contactId).filter((id) => id !== noteId)
        );
      }

      const m = metaRef.current;
      fetchNotes(m.current_page, m.per_page);
    },
    [contactId, fetchNotes]
  );

  // Computed values
  const hasActiveFilters = useMemo(() => {
    return filters.search !== '' || filters.category !== 'all';
  }, [filters]);

  const selectedCount = useMemo(() => selectedIds.size, [selectedIds]);

  const stateValue = useMemo(
    () => ({
      notes,
      meta,
      loading,
      error,
      filters,
      selectedIds,
      selectAll,
      hasActiveFilters,
      selectedCount,
      contactId,
    }),
    [
      notes,
      meta,
      loading,
      error,
      filters,
      selectedIds,
      selectAll,
      hasActiveFilters,
      selectedCount,
      contactId,
    ]
  );

  const actionsValue = useMemo(
    () => ({
      fetchNotes,
      updateFilters,
      clearFilters,
      toggleSelect,
      toggleSelectAll,
      clearSelection,
      handlePageChange,
      handleItemsPerPageChange,
      createNote: createNoteHandler,
      getNoteDetail: getNoteDetailHandler,
      updateNote: updateNoteHandler,
      deleteNote: deleteNoteHandler,
      pinNote: pinNoteHandler,
      archiveNote: archiveNoteHandler,
    }),
    [
      fetchNotes,
      updateFilters,
      clearFilters,
      toggleSelect,
      toggleSelectAll,
      clearSelection,
      handlePageChange,
      handleItemsPerPageChange,
      createNoteHandler,
      getNoteDetailHandler,
      updateNoteHandler,
      deleteNoteHandler,
      pinNoteHandler,
      archiveNoteHandler,
    ]
  );

  return (
    <NotesTabStateContext.Provider value={stateValue}>
      <NotesTabActionsContext.Provider value={actionsValue}>{children}</NotesTabActionsContext.Provider>
    </NotesTabStateContext.Provider>
  );
};

export const useNotesTabState = () => {
  const context = useContext(NotesTabStateContext);
  if (!context) {
    throw new Error('useNotesTabState must be used within NotesTabProvider');
  }
  return context;
};

export const useNotesTabActions = () => {
  const context = useContext(NotesTabActionsContext);
  if (!context) {
    throw new Error('useNotesTabActions must be used within NotesTabProvider');
  }
  return context;
};

// Backward-compatible hook (prefer useNotesTabState/useNotesTabActions).
export const useNotesTab = () => ({
  ...useNotesTabState(),
  ...useNotesTabActions(),
});

export default NotesTabStateContext;
